import React from "react";
import moment from "moment";
import Autocomplete from '@mui/material/Autocomplete';
import HomeMatchResultBadge from "./HomeMatchResultBadge";

function HomeTopCardsSection({ listadoJugadores, listadoTecnicos, navigate, ultimoPartido, revealed }) {
    const renderSearchInput = (params, placeholder) => (
        <div ref={params.InputProps.ref} className="flex items-center rounded-lg border border-white/10 bg-white/5 px-3 py-2 focus-within:border-green-500/60 transition-colors">
            <span className="text-slate-400 mr-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                </svg>
            </span>
            <input
                type="text"
                {...params.inputProps}
                placeholder={placeholder}
                className="w-full bg-transparent text-slate-100 placeholder:text-slate-500 focus:outline-none"
            />
        </div>
    );

    const getScoreDisplay = (partido) => {
        const aguadaScore = Number(partido?.totalAguadaStats?.totalPuntos);
        const rivalScore = Number(partido?.totalAdversarioStats?.totalPuntos);

        if (Number.isNaN(aguadaScore) || Number.isNaN(rivalScore)) {
            return <p className="text-lg font-bold mb-2 text-white tracking-wide">{partido.resultado}</p>;
        }

        const localTeam = partido.local ? 'Aguada' : partido.adversario;
        const awayTeam = partido.local ? partido.adversario : 'Aguada';
        const localScore = partido.local ? aguadaScore : rivalScore;
        const awayScore = partido.local ? rivalScore : aguadaScore;

        const localScoreClass = localScore > awayScore ? 'text-green-400' : localScore < awayScore ? 'text-red-400' : 'text-white';
        const awayScoreClass = awayScore > localScore ? 'text-green-400' : awayScore < localScore ? 'text-red-400' : 'text-white';

        return (
            <p className="text-lg font-bold mb-2 text-white tracking-wide">
                <span>{localTeam} </span>
                <span className={localScoreClass}>{localScore}</span>
                <span> - </span>
                <span className={awayScoreClass}>{awayScore}</span>
                <span> {awayTeam}</span>
            </p>
        );
    };

    return (
        <>
            <div
                className="relative overflow-hidden bg-white/5 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl p-5 sm:p-8 flex flex-col justify-between transition-all duration-700 ease-out"
                style={{ opacity: revealed ? 1 : 0, transform: revealed ? 'translateX(0)' : 'translateX(-2rem)' }}
            >
                <span className="pointer-events-none absolute left-0 top-0 h-0.5 w-1/2 bg-green-400"></span>
                <span className="pointer-events-none absolute bottom-0 right-0 h-0.5 w-1/3 bg-red-500"></span>
                <div>
                    <h3 className="text-white font-bold uppercase tracking-widest text-sm mb-6 drop-shadow-sm">Búsqueda rápida</h3>
                    <div className="space-y-4">
                        <div className="relative group">
                            <p className="text-slate-300 text-xs uppercase tracking-wider mb-2 text-left">Buscar jugador</p>
                            <Autocomplete
                                id="input-jugadores"
                                options={listadoJugadores}
                                onChange={(event, value) => {
                                    if (!value) return;
                                    setTimeout(() => navigate('jugador/' + value), 250);
                                }}
                                renderInput={(params) => renderSearchInput(params, "Buscar jugador...")}
                            />
                        </div>
                        <div className="relative group">
                            <p className="text-slate-300 text-xs uppercase tracking-wider mb-2 text-left">Buscar técnico</p>
                            <Autocomplete
                                id="input-tecnicos"
                                options={listadoTecnicos}
                                onChange={(event, value) => {
                                    if (!value) return;
                                    setTimeout(() => navigate('jugador/' + value), 250);
                                }}
                                renderInput={(params) => renderSearchInput(params, "Buscar técnico...")}
                            />
                        </div>
                    </div>
                </div>
                <p className="mt-6 text-xs text-slate-400 font-medium text-left">Encuentre perfiles detallados, trayectorias y promedios históricos.</p>
            </div>

            <div
                className="relative overflow-hidden bg-white/5 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl p-5 sm:p-8 transition-all duration-700 ease-out"
                style={{ opacity: revealed ? 1 : 0, transform: revealed ? 'translateY(0)' : 'translateY(1rem)', transitionDelay: revealed ? '100ms' : '0ms' }}
            >
                <span className="pointer-events-none absolute left-0 top-0 h-0.5 w-1/2 bg-green-400"></span>
                <span className="pointer-events-none absolute bottom-0 right-0 h-0.5 w-1/3 bg-red-500"></span>
                <h3 className="text-white font-bold uppercase tracking-widest text-sm mb-6 text-center drop-shadow-sm">Último Partido</h3>
                <div className="text-center">
                    {ultimoPartido.map((ultPart) =>
                        <React.Fragment key={ultPart._id}>
                            <p className="text-slate-400 text-sm font-semibold mb-1">{moment(ultPart.fecha).format('DD/MM/YYYY')}</p>
                            <p className="text-white font-medium mb-4 text-sm">{ultPart.liga} - {ultPart.temporadaRegular ? "Temp. Reg. Fecha " + ultPart.fechaLiga :
                                ultPart.playoffsEtapa ? ultPart.playoffsEtapa + " Partido " + ultPart.fechaLiga :
                                    ultPart.superLiga ? "SuperLiga Fecha " + ultPart.fechaLiga :
                                        ultPart.liguilla ? "Liguilla Fecha " + ultPart.fechaLiga :
                                            ultPart.reclasificatorio ? "Reclasificatorio Fecha " + ultPart.fechaLiga :
                                                ultPart.fase + " Partido " + ultPart.fechaLiga}</p>
                            {getScoreDisplay(ultPart)}
                            <p className="text-slate-400 text-xs mb-6 uppercase tracking-wider font-semibold">En {ultPart.cancha} como {ultPart.local ? 'Local' : 'Visitante'}</p>
                            <div className="mb-6">
                                <HomeMatchResultBadge partido={ultPart} />
                            </div>
                            <a href={`partido/${ultPart._id}`} className="group relative inline-flex w-full items-center justify-between overflow-hidden rounded-xl border border-white/25 bg-white/5 py-1.5 pl-4 text-sm font-bold uppercase tracking-wide text-white transition-all duration-300 hover:border-white/50 hover:bg-white/10">
                                <span className="absolute left-0 top-0 h-0.5 w-1/2 bg-green-400"></span>
                                <span className="absolute bottom-0 right-0 h-0.5 w-1/3 bg-red-500"></span>
                                <span>Ver estadísticas</span>
                                <span className="flex h-9 w-10 items-center justify-center text-xl text-green-400 transition-all duration-300 group-hover:translate-x-1 group-hover:text-red-400" aria-hidden="true">&#8594;</span>
                            </a>
                        </React.Fragment>
                    )}
                </div>
            </div>
        </>
    );
}

export default HomeTopCardsSection;
